/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import * as loginActions from "@/src/store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "@/src/services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { advocardFlexCheckinStyles } from "./advocardFlexCheckinStyle";
import AdvocardFlexCheckinForm from "./AdvocardFlexCheckinForm";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { apiPost } from "@/src/services/apiClient";
import { Box } from "@mui/material";
import { getCaseLink } from "@/src/services/productService";
import { gaEvent } from "@/src/services/googleAnalyticsService";

type AdvocardFlexCheckinProps = {
  idPrefix: string;
  mode?: "register" | "validate";
  validateCallback?: Function | null;
};

export type CreateFlexCaseRequest = {
  email: string;
  gclid: string | null;
  productClassName: string;
  partyType: string;
  affiliateCode: string | null;
  postalCode: string;
  insurancePolicyNumber: string;
  landingPage: string;
  isQuestionCurrentFlexServiceRelated: boolean | null;
  customerQuestion: string | null;
};

const AdvocardFlexCheckin = ({ idPrefix, mode = "register", validateCallback = null }: AdvocardFlexCheckinProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const { isLoggedIn, customer } = useCustomer();
  const initialValues = {
    productClassName: "purchaseContract",
    partyType: null,
    email: isLoggedIn ? customer!.email : "",
    insurancePolicyNumber: "",
    postalCode: "",
  };

  useEffect(() => {
    if (isLoggedIn) {
      setEmailErrorText("");
    }
  }, [isLoggedIn]);

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);
    setSubmitErrorMessage("");

    if (!isLoggedIn) {
      const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
      if (emailCheck.hasError) {
        setEmailErrorText(emailCheck.errorText);
        setIsLoading(false);
        return;
      }
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };

    try {
      const createCaseRequest: CreateFlexCaseRequest = {
        email: values.email.toLowerCase(),
        gclid: localStorage.getItem("gclid"),
        productClassName: values.productClassName,
        partyType: values.partyType,
        affiliateCode: localStorage.getItem("gclid"),
        postalCode: values.postalCode,
        insurancePolicyNumber: values.insurancePolicyNumber,
        landingPage: landingPage.keyword,
        isQuestionCurrentFlexServiceRelated: true,
        customerQuestion: null,
      };

      if (mode === "validate") {
        const result = await apiPost("/advocard/flex/validate_account", createCaseRequest);

        if (validateCallback) {
          validateCallback(result["status"] === "success", createCaseRequest);
        }
      } else {
        const result = await apiPost("/advocard/flex/create_case", createCaseRequest);

        if (!isLoggedIn) {
          localStorage.setItem("token", result.token);
          gaEvent({
            category: "Registration",
            action: "Account created",
          });
        }
        await queryClient.invalidateQueries({
          queryKey: queryKeys.collection("/customers"),
        });

        navigate(getCaseLink(result.case) + "/antrag");
      }
    } catch (error) {
      setSubmitErrorMessage(
        "Die angegebenen Informationen konnten nicht verifiziert werden oder Sie sind nicht berechtigt, diesen Service zu nutzen. Gerne beraten wir Sie dazu unter 040 237310."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box id={idPrefix + "-checkin"} />
      <Paper elevation={4} sx={advocardFlexCheckinStyles.formPaper}>
        <Typography variant={"h5"} sx={{
          textAlign: "center"
        }}>
          Zum FlexService anmelden
        </Typography>
        <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
          <AdvocardFlexCheckinForm
            isLoading={isLoading}
            emailErrorText={emailErrorText}
            setEmailErrorText={setEmailErrorText}
            externalError={submitErrorMessage}
          />
        </FormProvider>
      </Paper>
    </>
  );
};

export default AdvocardFlexCheckin;
